import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, HeartPulse, Gem } from "lucide-react";
import { BRAND, IMAGES } from "../lib/site";
import { SectionHeading } from "../components/Sections";

const services = [
  {
    icon: Sparkles,
    title: "Brazilian Butt Lift",
    desc: "Sculpted curves through advanced fat transfer artistry, designed in harmony with your natural silhouette.",
    path: "/services",
  },
  {
    icon: Gem,
    title: "Breast Enhancement",
    desc: "Bespoke implant selection — from teardrop saline to premium silicone — for refined, balanced femininity.",
    path: "/services",
  },
  {
    icon: HeartPulse,
    title: "Concierge Recovery",
    desc: "A private resort-style suite with 24/7 nursing care, lymphatic massage and luxurious post-op rituals.",
    path: "/recovery",
  },
];

const stats = [
  { value: "20+", label: "Years of Surgical Craft" },
  { value: "5K+", label: "Refined Silhouettes" },
  { value: "24/7", label: "Concierge Recovery" },
  { value: "100%", label: "Discretion Promised" },
];

export default function HomePage() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={IMAGES.heroHome} alt="" className="w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/30 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full grid md:grid-cols-12 gap-8 items-center pt-10 pb-24">
          <div className="md:col-span-8">
            <span className="text-[11px] tracking-[0.45em] uppercase text-gold font-body" data-testid="hero-kicker">
              Raleigh · Casper · Aesthetic Surgery
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white leading-[0.98] mt-6">
              The Art of the{" "}
              <span className="font-brand text-pink-luxe text-6xl sm:text-7xl md:text-8xl lg:text-9xl block">
                Sculpted Silhouette
              </span>
            </h1>
            <p className="font-heading text-white/80 text-xl md:text-2xl mt-8 max-w-2xl leading-relaxed">
              {BRAND.name} is a private atelier for body contouring,
              breast artistry and indulgent recovery — where surgical
              precision meets resort-grade serenity.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/consultations" className="btn-gold" data-testid="hero-cta-consult">
                Reserve Consultation <ArrowRight size={16} />
              </Link>
              <Link to="/services" className="btn-outline-gold" data-testid="hero-cta-services">
                Explore Procedures
              </Link>
            </div>
          </div>

          <div className="md:col-span-4 hidden md:flex justify-end">
            <div className="border border-[rgba(212,175,55,0.3)] bg-black/40 backdrop-blur-md p-8 max-w-xs">
              <p className="font-brand text-gold text-4xl leading-none">Est. Excellence</p>
              <div className="gold-divider my-5" />
              <p className="text-white/75 font-heading italic text-lg leading-relaxed">
                "Where every curve is composed with the patience of a sculptor and the discretion of a private salon."
              </p>
              <p className="text-pink-luxe text-[11px] tracking-[0.3em] uppercase mt-6">
                — Patient Memoir
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-32 bg-mag-2 relative grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <img src={IMAGES.floral} alt="" className="w-full h-[460px] object-cover" />
          </div>
          <div className="md:col-span-7">
            <SectionHeading
              kicker="A Philosophy of Beauty"
              title="Couture surgery for the"
              accent="modern muse"
              testid="intro-heading"
            />
            <p className="text-white/75 font-heading text-lg md:text-xl mt-8 leading-relaxed">
              We believe transformation should feel as elegant as it looks.
              From the first whispered consultation to the final stitch and
              the candlelit recovery suite, every detail at {BRAND.name} is
              composed to honour your body and your privacy.
            </p>
            <div className="grid grid-cols-2 gap-6 mt-10">
              <div className="border-l border-[rgba(212,175,55,0.4)] pl-5">
                <ShieldCheck className="text-gold mb-3" size={22} />
                <p className="font-heading text-white text-xl">Board-Certified Care</p>
                <p className="text-white/55 text-sm mt-1">Accredited surgical suites, anesthesiology and recovery staff.</p>
              </div>
              <div className="border-l border-[rgba(248,200,220,0.5)] pl-5">
                <Gem className="text-pink-luxe mb-3" size={22} />
                <p className="font-heading text-white text-xl">Bespoke Aesthetic</p>
                <p className="text-white/55 text-sm mt-1">Designed in 3D imaging to honour your natural lines.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-mag relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            kicker="Signature Services"
            title="The"
            accent="House Specialties"
            align="center"
            testid="services-heading"
          />
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-16">
            {services.map((s, i) => (
              <Link
                to={s.path}
                key={i}
                data-testid={`home-service-card-${i}`}
                className="mag-card p-10 relative group block"
              >
                <s.icon size={28} className="text-gold mb-6" />
                <h3 className="font-heading text-3xl text-white mb-3">{s.title}</h3>
                <p className="text-white/65 leading-relaxed text-[15px]">{s.desc}</p>
                <span className="absolute bottom-8 right-8 text-gold opacity-0 group-hover:opacity-100 transition">
                  <ArrowRight size={20} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 bg-mag-2 border-y border-[rgba(212,175,55,0.18)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-10">
          {stats.map((s, i) => (
            <div key={i} className="text-center" data-testid={`home-stat-${i}`}>
              <p className="font-brand text-gold text-6xl md:text-7xl leading-none">{s.value}</p>
              <p className="text-white/55 text-[11px] tracking-[0.3em] uppercase mt-3">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-24 md:py-32 bg-mag relative">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <span className="text-[11px] tracking-[0.4em] uppercase text-gold font-body">Patient Voices</span>
          <p className="font-heading italic text-2xl md:text-3xl text-white/85 mt-8 leading-relaxed">
            "Walking into Medical Arts Groves felt like stepping into a
            five-star atelier. The artistry, the calm, the way they made me
            feel cherished — it changed everything."
          </p>
          <p className="font-brand text-pink-luxe text-4xl mt-8">Sienna R.</p>
          <p className="text-white/45 text-[11px] tracking-[0.3em] uppercase mt-1">BBL & Recovery Suite Client</p>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-36 overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={IMAGES.abstractGold} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/70" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-heading text-5xl md:text-6xl text-white">
            Begin your{" "}
            <span className="font-brand text-pink-luxe text-6xl md:text-7xl">private consultation</span>
          </h2>
          <p className="font-heading text-white/70 text-lg mt-6 max-w-2xl mx-auto">
            Reserve a discreet evaluation with our surgical concierge.
            Every story begins with a conversation.
          </p>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to="/consultations" className="btn-gold" data-testid="bottom-cta-consult">
              Book Now <ArrowRight size={16} />
            </Link>
            <a href={`tel:+1${BRAND.phoneHref}`} className="btn-outline-gold" data-testid="bottom-cta-call">
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
